import axios from 'axios';
import config from './config';
import { setupClientIdInterceptor, setupAuthGuardTokenInterceptor } from './apiInterceptor';

// Flag to detect Docker environment
const isDockerEnvironment = () => {
  // For browser environment, we NEVER want to use Docker container names directly
  // as the browser cannot resolve them
  if (typeof window !== 'undefined') {
    console.log('Browser environment detected - will use localhost URLs');
    return false;
  }
  
  // These checks only matter for server-side rendering which we're not using
  // Look for explicit environment variable first
  if (import.meta.env.VITE_CONTAINER_ENVIRONMENT === 'true') {
    console.log('Docker environment detected via VITE_CONTAINER_ENVIRONMENT');
    return true;
  }
  
  // Default to non-Docker for safety
  return false;
};

// Manual override for testing purposes
export const setDockerEnvironment = (isDocker: boolean) => {
  try {
    localStorage.setItem('container_environment', isDocker ? 'true' : 'false');
    console.log(`Manual Docker environment detection set to: ${isDocker}`);
    // Force reload page to recreate API clients
    window.location.reload();
  } catch (e) {
    console.error('Failed to set Docker environment:', e);
  }
};

// Map original URLs to local dev ports
const portMap: { [key: string]: string } = {
  // Standard module container names - using single implementation now (db mode integrated)
  'module-snowflake:8000': '8000',           // Snowflake module on port 8000
};

// Modify URLs to prioritize DB API endpoints and handle module3 integration

// All modules now route through Snowflake BFF - no URL modifications needed

// Create axios instances for each module
const createApiClient = (baseURL: string) => {
  console.log('Original API URL:', baseURL);
  
  // In Azure Container Apps, use the URLs as-is (they should already be correct from config)
  if (config.environment.isContainer && !baseURL.includes('localhost')) {
    console.log('Container environment detected, using URL as-is:', baseURL);
    const client = axios.create({
      baseURL,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    // Add API key to requests if enabled
    if (config.api.keyEnabled && config.api.key) {
      client.interceptors.request.use((requestConfig) => {
        requestConfig.headers['X-API-Key'] = config.api.key;
        return requestConfig;
      });
    }

    return client;
  }
  
  // Check if using a full URL (starts with http:// or https://)
  if (baseURL.startsWith('http://') || baseURL.startsWith('https://')) {
    try {
      const url = new URL(baseURL);
      const docker = isDockerEnvironment();
      console.log(`Docker environment detection result: ${docker}`);
      
      // If this is a browser environment, ALWAYS convert container hostnames to localhost
      // Extract hostname:port from URL 
      const hostPort = `${url.hostname}:${url.port}`;
      console.log(`Checking URL, hostPort: ${hostPort}`);
      
      // If this is a container URL, map to correct local port
      if (portMap[hostPort]) {
        console.log(`Port mapping found: ${hostPort} → localhost:${portMap[hostPort]}`);
        url.hostname = 'localhost';
        url.port = portMap[hostPort];
      } else if (url.hostname !== 'localhost' && url.hostname !== '127.0.0.1') {
        // For any non-localhost hostname, convert to localhost and keep port
        console.log(`Non-localhost hostname detected: ${url.hostname}, converting to localhost`);
        url.hostname = 'localhost';
      }
      
      baseURL = url.toString();
      console.log('Transformed API URL:', baseURL, docker ? '(Docker)' : '(Local)');
    } catch (error) {
      console.error('Error transforming API URL:', error);
    }
  }

  const client = axios.create({
    baseURL,
    timeout: 300000, // 5 minutes for file uploads
    headers: {
      'Content-Type': 'application/json',
    },
  });

  // Add API key to requests if enabled
  if (config.api.keyEnabled && config.api.key) {
    client.interceptors.request.use((requestConfig) => {
      requestConfig.headers['X-API-Key'] = config.api.key;
      return requestConfig;
    });
  }

  return client;
};

// Create API clients - only Snowflake BFF needed now
export const snowflakeApi = createApiClient(config.api.snowflake);

// Apply client ID interceptor to snowflakeApi which now handles all client-specific data
setupClientIdInterceptor(snowflakeApi);
console.log('Client ID interceptor applied to snowflakeApi');

// Apply AuthGuard token interceptor to attach Authorization header from localStorage
setupAuthGuardTokenInterceptor(snowflakeApi);
console.log('AuthGuard token interceptor applied to snowflakeApi');

// Module2 now uses Snowflake BFF - no separate interceptor needed

// Helper function to wait for a specified time
const wait = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

// Add response interceptors to handle workflow external ID not found errors
// This helps with the race condition when creating workflows
snowflakeApi.interceptors.response.use(
  (response) => response,
  async (error) => {
    // Check if this is a 404 for external workflow ID
    if (error.response && 
        error.response.status === 404 && 
        error.config.url && 
        error.config.url.includes('/db/workflow/external/')) {
      
      console.log('External workflow ID not found, trying to fetch by numeric ID as fallback');
      
      // Extract the external ID from the URL
      const urlParts = error.config.url.split('/');
      const externalId = urlParts[urlParts.length - 1];
      
      // Set up retry parameters
      const maxRetries = 5;
      const baseDelay = 1000; // 1 second initial delay
      
      try {
        // Try fetching all workflows for this client
        const clientId = error.config.params?.client_id;
        if (clientId) {
          console.log(`Attempting fallback lookup for workflow ${externalId} with client ${clientId}`);
          
          // Try multiple times with increasing delays
          for (let attempt = 0; attempt < maxRetries; attempt++) {
            // Fetch workflows list for this client
            const fallbackResponse = await snowflakeApi.get('/api/v1/db/workflows', {
              params: { client_id: clientId }
            });
            
            // Look for the workflow with the matching external ID
            if (fallbackResponse.data && Array.isArray(fallbackResponse.data)) {
              console.log(`Found ${fallbackResponse.data.length} workflows for client ${clientId}`);
              
              // Debug info to see what's in the database
              if (attempt === 0) {
                console.log('Available workflows:', fallbackResponse.data.map(w => 
                  `ID: ${w.workflow_id}, External ID: ${w.external_id}`
                ));
              }
              
              const workflow = fallbackResponse.data.find(w => w.external_id === externalId);
              
              if (workflow) {
                console.log(`Found workflow in list with ID ${workflow.workflow_id}, external ID ${workflow.external_id}`);
                return { data: workflow, status: 200, statusText: 'OK', headers: fallbackResponse.headers, config: error.config };
              }
            }
            
            // Wait with exponential backoff before trying again
            if (attempt < maxRetries - 1) {
              const delay = baseDelay * Math.pow(2, attempt);
              console.log(`Workflow not found in list, retrying in ${delay}ms (attempt ${attempt + 1}/${maxRetries})`);
              await wait(delay);
            }
          }
          
          // If we've exhausted our retries, try fetching the workflow directly by ID
          console.log('Exhausted retries for workflow list, attempting direct numeric ID lookup');
          
          // As a last resort, try to guess the numeric ID - it might be a sequential number
          // or we could try fetching the latest workflow
          try {
            // Try fetching the latest workflow
            const latestResponse = await snowflakeApi.get('/api/v1/db/workflows', {
              params: { 
                client_id: clientId,
                limit: 1,
                sort: 'created_at:desc'
              }
            });
            
            if (latestResponse.data && 
                Array.isArray(latestResponse.data) && 
                latestResponse.data.length > 0 &&
                latestResponse.data[0].external_id === externalId) {
              
              const workflow = latestResponse.data[0];
              console.log(`Found latest workflow with ID ${workflow.workflow_id}, external ID ${workflow.external_id}`);
              return { data: workflow, status: 200, statusText: 'OK', headers: latestResponse.headers, config: error.config };
            }
          } catch (latestError) {
            console.error('Latest workflow lookup failed:', latestError);
          }
        }
      } catch (fallbackError) {
        console.error('Fallback workflow lookup failed:', fallbackError);
      }
      
      // If we still can't find the workflow, create a temporary local placeholder
      // This allows the UI to show something while waiting for backend sync
      console.log(`Creating temporary placeholder for workflow ${externalId}`);
      
      try {
        // Extract client_id from the request parameters
        const clientId = error.config.params?.client_id;
        
        // Get current time for timestamps
        const now = new Date().toISOString();
        
        // Create a placeholder response to prevent app crashing
        const placeholderWorkflow = {
          workflow_id: -1, // Negative ID indicates placeholder
          external_id: externalId,
          name: "Workflow loading...",
          description: "This workflow is being synchronized. Please refresh in a few moments.",
          status: "syncing", // Custom status to indicate it's not fully loaded
          created_at: now,
          updated_at: now,
          is_active: true,
          client: clientId ? { client_id: clientId, name: clientId } : null,
          modules: [],
          module_counts: {},
          total_modules: 0,
          _placeholder: true // Special flag to indicate this is a placeholder
        };
        
        return {
          data: placeholderWorkflow,
          status: 202, // Using 202 Accepted to indicate it's processing
          statusText: 'Accepted',
          headers: {},
          config: error.config
        };
      } catch (placeholderError) {
        console.error('Failed to create placeholder workflow:', placeholderError);
      }
    }
    
    // If fallback didn't work or wasn't applicable, reject with original error
    return Promise.reject(error);
  }
);

// Define API endpoints
export const API_ENDPOINTS = {
  SNOWFLAKE: config.api.snowflake,
};

// Function to get API endpoints
export const getApiEndpoints = () => API_ENDPOINTS;

// Export all API clients
export default {
  snowflake: snowflakeApi,
};
